import { trainingDecisionSchema, type TrainingDecision } from '@cadencia/contracts';
import { expiryFor, isExpired, type DecisionRecord } from '@cadencia/domain';
import { db } from '../db.js';
import { nowIso } from '../ids.js';
import { newSyncColumns, putNewRows, putRow, type WriteContext } from '../writes.js';
import { parchear } from '../parchear.js';

/**
 * Registro de decisiones y panel de aprobacion (entregable 3.10).
 *
 * Las decisiones se guardan SIEMPRE, pidan aprobacion o no. Guardar solo las
 * que preguntan dejaria sin rastro a los ajustes automaticos, que son la
 * mayoria, y el registro dejaria de servir para lo que existe: comprobar si el
 * sistema acierta.
 */

/** Fila del servidor a la forma que maneja el dominio. */
export function decisionDesdeFila(fila: TrainingDecision): DecisionRecord {
  return {
    decisionType: fila.decisionType,
    inputs: fila.inputs,
    previousValue: fila.previousValue,
    newValue: fila.newValue,
    source: fila.source,
    reasonCodes: fila.reasonCodes,
    reasonText: fila.reasonText,
    confidence: fila.confidence,
    requiresApproval: fila.requiresApproval,
    approvalStatus: fila.approvalStatus,
    decidedAt: fila.decidedAt,
    // Derivada, no almacenada: misma formula que al crearla.
    expiresOn: expiryFor(fila.decidedAt, fila.requiresApproval),
  };
}

/**
 * Guarda una tanda de propuestas.
 *
 * Devuelve las filas creadas. Si la tanda viene vacia no toca la base: una
 * transaccion sin contenido solo sirve para generar ruido en el outbox.
 */
export async function saveDecisions(
  ctx: WriteContext,
  records: readonly DecisionRecord[],
): Promise<TrainingDecision[]> {
  if (records.length === 0) return [];

  const filas = records.map((record) =>
    trainingDecisionSchema.parse({
      ...newSyncColumns(ctx),
      decisionType: record.decisionType,
      decidedAt: record.decidedAt,
      inputs: record.inputs,
      previousValue: record.previousValue,
      newValue: record.newValue,
      source: record.source,
      reasonCodes: record.reasonCodes,
      reasonText: record.reasonText,
      confidence: record.confidence,
      requiresApproval: record.requiresApproval,
      approvalStatus: record.approvalStatus,
      approvedAt: null,
      evaluateAfter: null,
      outcome: null,
      outcomeEvaluatedAt: null,
    }),
  );

  await putNewRows(
    ctx,
    filas.map((row) => ({ table: 'trainingDecisions' as const, row })),
  );
  return filas;
}

async function todasDelUsuario(userId: string): Promise<TrainingDecision[]> {
  const filas = await db.trainingDecisions.where('userId').equals(userId).toArray();
  return filas.filter((fila) => fila.deletedAt === null);
}

/** Lo que espera respuesta ahora mismo, lo mas reciente primero. */
export async function listPendingDecisions(
  userId: string,
  now = nowIso(),
): Promise<TrainingDecision[]> {
  const filas = await todasDelUsuario(userId);
  return filas
    .filter((fila) => fila.approvalStatus === 'pending')
    .filter((fila) => !isExpired(decisionDesdeFila(fila), now))
    .sort((a, b) => b.decidedAt.localeCompare(a.decidedAt));
}

/** Historial completo, lo mas reciente primero. */
export async function listRecentDecisions(
  userId: string,
  limit = 50,
): Promise<TrainingDecision[]> {
  const filas = await todasDelUsuario(userId);
  return filas.sort((a, b) => b.decidedAt.localeCompare(a.decidedAt)).slice(0, limit);
}

async function cambiarEstado(
  ctx: WriteContext,
  fila: TrainingDecision,
  approvalStatus: TrainingDecision['approvalStatus'],
  approvedAt: string | null,
): Promise<TrainingDecision> {
  return putRow(
    ctx,
    'trainingDecisions',
    parchear(trainingDecisionSchema, fila, { approvalStatus, approvedAt }, {
      tabla: 'training_decisions',
      id: fila.id,
    }),
  );
}

export async function approveDecision(
  ctx: WriteContext,
  fila: TrainingDecision,
): Promise<TrainingDecision> {
  return cambiarEstado(ctx, fila, 'approved', nowIso());
}

export async function rejectDecision(
  ctx: WriteContext,
  fila: TrainingDecision,
): Promise<TrainingDecision> {
  // Sin `approvedAt`: rechazar no es aprobar en otro momento.
  return cambiarEstado(ctx, fila, 'rejected', null);
}

/**
 * Marca como caducadas las pendientes que pasaron de plazo.
 *
 * Se ejecuta al abrir el panel y no por un temporizador: si nadie mira, no hay
 * nada que decidir, y un proceso de fondo tocando filas sin que nadie lo pida
 * genera sincronizaciones que a nadie le sirven.
 */
export async function expireDueDecisions(ctx: WriteContext, now = nowIso()): Promise<number> {
  const filas = await todasDelUsuario(ctx.userId);
  const caducadas = filas
    .filter((fila) => fila.approvalStatus === 'pending')
    .filter((fila) => isExpired(decisionDesdeFila(fila), now));

  for (const fila of caducadas) {
    await cambiarEstado(ctx, fila, 'expired', null);
  }
  return caducadas.length;
}

/**
 * Guarda una propuesta de la IA dentro de la decision que espera aprobacion.
 *
 * Va en `inputs` y no en una tabla nueva porque **es una entrada de esa
 * decision**: describe con que parametros se aplicaria el cambio de fase que
 * el usuario tiene delante. Sacarla a otro sitio obligaria a mantener viva la
 * relacion entre las dos filas, y a decidir que pasa con la propuesta cuando la
 * decision caduca a los 7 dias. Aqui caduca con ella, que es lo correcto.
 *
 * Solo se escribe sobre decisiones PENDIENTES: una ya aplicada no admite
 * cambiar los parametros con los que se aplico.
 */
export async function attachDecisionInputs(
  ctx: WriteContext,
  decisionId: string,
  patch: Record<string, unknown>,
): Promise<TrainingDecision | null> {
  const fila = await db.trainingDecisions.get(decisionId);
  if (!fila || fila.deletedAt !== null) return null;
  if (fila.approvalStatus !== 'pending') return null;

  return putRow(
    ctx,
    'trainingDecisions',
    parchear(
      trainingDecisionSchema,
      fila,
      { inputs: { ...fila.inputs, ...patch } },
      { tabla: 'training_decisions', id: fila.id },
    ),
  );
}
